import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog } from './Dialog';
import { ScanLine } from 'lucide-react';

function extractImei(data: string): string | null {
	const match = data.match(/\b(\d{15})\b/);
	return match ? match[1] : null;
}

export default function QrImeiScanner({ onScan, onClose }: { onScan: (imei: string) => void; onClose: () => void }) {
	const scannerRef = useRef<Html5Qrcode | null>(null);
	const [error, setError] = useState('');
	const containerIdRef = useRef('qr-imei-scanner-' + Date.now());
	const hasScannedRef = useRef(false);

	useEffect(() => {
		const containerId = containerIdRef.current;
		let mounted = true;
		const isRunningRef = { current: false };

		const startScanner = async () => {
			try {
				const scanner = new Html5Qrcode(containerId);
				scannerRef.current = scanner;

				await scanner.start(
					{ facingMode: 'environment' },
					{ fps: 10, qrbox: { width: 300, height: 150 } },
					(decodedText: string) => {
						if (hasScannedRef.current) return;
						const imei = extractImei(decodedText);
						if (imei) {
							hasScannedRef.current = true;
							isRunningRef.current = false;
							scanner.stop().catch(() => {});
							onScan(imei);
						} else {
							setError('No se encontró un IMEI válido en el código QR. Intente de nuevo.');
						}
					},
					() => {}
				);
				isRunningRef.current = true;
			} catch (err) {
				if (mounted) {
					setError('No se pudo acceder a la cámara. Verifique los permisos del navegador.');
				}
			}
		};

		const timeout = setTimeout(startScanner, 300);

		return () => {
			mounted = false;
			clearTimeout(timeout);
			const scanner = scannerRef.current;
			if (scanner && isRunningRef.current) {
				isRunningRef.current = false;
				scanner.stop().catch(() => {});
			}
			scannerRef.current = null;
		};
	}, [onScan]);

	return (
		<Dialog
			title="Escanear IMEI"
			onClose={onClose}
			cerrarButton={false}
			className="!max-w-[min(500px,90vw)]"
		>
			<div className="flex flex-col items-center gap-4">
				<div className="flex items-center gap-2 text-sm text-neutral-300">
					<ScanLine className="size-4" />
					<span>Apunte la cámara al código QR de la caja del equipo</span>
				</div>

				<div
					id={containerIdRef.current}
					className="w-full overflow-hidden rounded-lg"
					style={{ minHeight: 280 }}
				/>

			<p className="text-center text-sm font-medium text-yellow-400">
				⚠ Asegúrese de escanear el código QR del IMEI 1
			</p>

			{error && (
				<p className="text-center text-sm text-red-400">{error}</p>
			)}
			</div>
		</Dialog>
	);
}
